import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useGlobal } from "../global/GlobalContext";
import API_BASE_URL from "../global/apiConfig";
import WishlistButton from "./WishlistButton";
import NotFound from "../pages/NotFound";
import { BiChevronRight } from "react-icons/bi";
import { FiFilter, FiChevronDown, FiChevronUp, FiX, FiPackage } from "react-icons/fi";
import { LazyLoadImage } from "react-lazy-load-image-component";
import compressImage from "../utils/compressImage";
import { UserRole } from "../config/userRole";

function ProductPageBody({ categorySlug, ProductsCount, products, catagoryProducts }) {

  const { token, wishlist, setWishlist } = useGlobal();
  const [sortOption, setSortOption] = useState("popularity");
  const [isOpen, setIsOpen] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [brands, setBrands] = useState([]);
  const [colors, setColors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({ size: [], brand: [], color_id: [], category: [] });
  const navigate = useNavigate();
  const [showAllSizes, setShowAllSizes] = useState(false);
  const [showAllBrands, setShowAllBrands] = useState(false);
  const [showAllColors, setShowAllColors] = useState(false);
  const [showAllCategories, setShowAllCategories] = useState(false);


  useEffect(() => {

    let filtered = [];
    // Use category products if available, otherwise use all products
    const sourceProducts = catagoryProducts.products?.length > 0 ? catagoryProducts.products : products?.products;

    if (Array.isArray(sourceProducts)) {
      filtered = sourceProducts.filter((product) => {
        if (UserRole.userRole === "distributor") {
          return product.pack_price != null && product.pack_qty != null;
        }
        const matchesSize =
          !filters.size.length ||
          product.product_variations?.some((v) => filters.size.includes(v.size));

        const matchesBrand =
          !filters.brand.length || filters.brand.includes(product.brand_details?.brand_name);

        const matchesColor =
          !filters.color_id.length || (product.color_id && filters.color_id.includes(product.color_id));

        const matchesCategory =
          !filters.category.length || // No filter applied → show all
          product.product_categories?.some((v) => filters.category.includes(v.category_name));


        return matchesSize && matchesBrand && matchesColor && matchesCategory;
      });
    }

    setFilteredProducts(filtered);
  }, [filters, products, catagoryProducts]);

  const filterColor = useCallback(async () => {

    // Use category products if available, otherwise use all products
    const sourceProducts = catagoryProducts.products?.length > 0 ? catagoryProducts.products : products?.products;
    const uniqueColorsMap = new Map();
    sourceProducts?.forEach((product) => {
      const color = product?.color_details;
      if (color?.id) {
        uniqueColorsMap.set(color.id, {
          color_id: Number(color.id),
          color_name: color.color_code.startsWith("#") ? color.color_code : color.color_code.toLowerCase(),
        });
      }

    });

    const uniqueColors = Array.from(uniqueColorsMap.values());
    setColors(uniqueColors);
  }, [catagoryProducts.products, products?.products]);

  const filterSizes = useCallback(async () => {
    // Use category products if available, otherwise use all products
    const sourceProducts = catagoryProducts.products?.length > 0 ? catagoryProducts.products : products?.products;
    const uniqueSizesMap = new Map();
    sourceProducts?.forEach((product) => {
      product.product_variations.forEach((variation) => {
        if (variation?.size_id && variation?.size) {
          uniqueSizesMap.set(variation.size_id, {
            size_id: Number(variation.size_id),
            size: variation.size ? variation.size : variation.size.toLowerCase(),
          });
        }
      });
    });

    const uniqueSizes = Array.from(uniqueSizesMap.values());
    setSizes(uniqueSizes);
  }, [catagoryProducts.products, products?.products]);

  const filterBrands = useCallback(async () => {
    // Use category products if available, otherwise use all products
    const sourceProducts = catagoryProducts.products?.length > 0 ? catagoryProducts.products : products?.products;
    const uniqueBrandsMap = new Map();
    sourceProducts?.forEach((brand) => {
      const b = brand?.brand_details;
      if (b?.id) {
        uniqueBrandsMap.set(b.id, {
          brand_id: Number(b.id),
          brand_name: b.brand_name ? b.brand_name : b.brand_name.toLowerCase(),
        });
      }
    });

    const uniqueBrands = Array.from(uniqueBrandsMap.values());
    setBrands(uniqueBrands);
  }, [catagoryProducts.products, products?.products]);

  const filterCategory = useCallback(async () => {
    // Use category products if available, otherwise use all products
    const sourceProducts = catagoryProducts.products?.length > 0 ? catagoryProducts.products : products?.products;
    const uniqueCategoryMap = new Map();
    sourceProducts?.forEach((cat) => {
      cat.product_categories.forEach((variation) => {
        if (variation?.id && variation?.category_name) {
          uniqueCategoryMap.set(variation.id, {
            category_id: Number(variation.id),
            category: variation.category_name ? variation.category_name : variation.category_name.toLowerCase(),
            category_slug: variation.slug,
            updated_at: cat.updated_at,
            sale_price: cat.sale_price,
            rating: cat.product_rating
          });
        }
      });
    });

    const uniqueCategories = Array.from(uniqueCategoryMap.values());
    setCategories(uniqueCategories);
  }, []);


  const handleSortChange = (event) => {
    const selectedSort = event.target.value;
    setSortOption(selectedSort);
    setIsOpen(false);

    let sortedProducts = [...filteredProducts];

    if (selectedSort === "popularity") {
      sortedProducts.sort((a, b) => Number(b.rating) - Number(a.rating));
    } else if (selectedSort === "newArrival") {
      sortedProducts.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
    } else if (selectedSort === "priceLowToHigh") {
      sortedProducts.sort((a, b) => parseFloat(a.sale_price) - parseFloat(b.sale_price));
    } else if (selectedSort === "priceHighToLow") {
      sortedProducts.sort((a, b) => parseFloat(b.sale_price) - parseFloat(a.sale_price));
    }

    setFilteredProducts(sortedProducts); // Update state once, no loop
  };

  const sortOptions = [
    { value: "popularity", label: "Popularity" },
    { value: "newArrival", label: "New Arrival" },
    { value: "priceLowToHigh", label: "Price: Low to High" },
    { value: "priceHighToLow", label: "Price: High to Low" }
  ];

  const selectedLabel = sortOptions.find(option => option.value === sortOption)?.label;


  useEffect(() => {
    filterColor();
    filterSizes();
    filterBrands();
    filterCategory();
  }, [filterColor, filterSizes, filterBrands, filterCategory]);



  const formattedCategory = categorySlug
    ? catagoryProducts.category_data?.category_name
    : "All Products";

  const toggleFilter = (filterType, value) => {
    setFilters((prevFilters) => {
      if (value === undefined || value === null) return prevFilters; // Prevent adding undefined values

      const currentValues = prevFilters[filterType] || [];
      const updatedValues = currentValues.includes(value)
        ? currentValues.filter((item) => item !== value) // Remove if already selected
        : [...currentValues, value]; // Add if not selected

      return { ...prevFilters, [filterType]: updatedValues };
    });
  };

  const getPackInfo = (product) => {
    const variation = product?.product_variations?.find(
      (v) => v?.pack_price != null && v?.pack_qty != null
    );
    const packPrice = variation?.pack_price ?? product?.pack_price;
    const packQty = variation?.pack_qty ?? product?.pack_qty;
    return { packPrice, packQty };
  };


  if (!products) {
    return <NotFound />;
  }

  return (
    <div className="product-page-body bg-gray-50 max-w-8xl mx-auto px-0 md:px-6 lg:px-8 mb-12 mt-4 pb-36 md:pb-4">
      {/* Breadcrumb */}
      <div className="breadcrumb mb-5 text-gray-400 md:flex items-center gap-2 hidden text-sm">
        <Link className="hover:text-brand-600 transition-colors" to="/">Home</Link>
        <BiChevronRight size={14} />
        <span className="uppercase font-medium text-gray-600">{formattedCategory}</span>
      </div>

      <div className="flex justify-between flex-col md:flex-row gap-6">
        {/* Filter Sidebar */}
        <div className="filter sticky w-full md:w-1/5 top-4 h-fit z-[60] md:z-0">
          <div className="hidden md:block rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-5">
              <FiFilter className="text-brand-600" size={16} />
              <span className="text-sm font-bold text-gray-900">Filters</span>
            </div>

            {/* Category Filter */}
            {categories && categories.length > 0 && (
              <div className="mb-5 pb-5 border-b border-gray-100">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Categories</p>
                <div className="flex flex-col items-start gap-2.5">
                  {categories.slice(0, showAllCategories ? categories.length : 4).map((cat) => (
                    <button
                      key={cat.category_id}
                      className="text-brand-600 font-medium text-sm text-left hover:underline"
                      onClick={() => navigate(`../../${cat.category_slug}`)}
                    >
                      {cat.category}
                    </button>
                  ))}

                  {categories.length > 4 && (
                    <button
                      onClick={() => setShowAllCategories(!showAllCategories)}
                      className="text-gray-400 mt-1 text-xs font-semibold hover:text-brand-600"
                    >
                      {showAllCategories ? "Show Less" : "Show More"}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Size Filter */}
            {sizes && sizes.length > 0 && (
              <div className="mb-5 pb-5 border-b border-gray-100">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Sizes</p>
                <div className="flex flex-col items-start gap-2.5">
                  {sizes.slice(0, showAllSizes ? sizes.length : 4).map((sizeObj) => (
                    <button
                      key={sizeObj.size_id}
                      className="flex items-center gap-2.5 text-sm text-gray-700"
                      onClick={() => toggleFilter("size", sizeObj.size)}
                    >
                      <span className={`w-4 h-4 rounded flex items-center justify-center border transition-colors
                        ${filters.size.includes(sizeObj.size) ? "bg-brand-600 border-brand-600" : "border-gray-300"}`}>
                        {filters.size.includes(sizeObj.size) && <span className="text-white text-[10px] leading-none">✓</span>}
                      </span>
                      {sizeObj.size}
                    </button>
                  ))}

                  {sizes.length > 4 && (
                    <button
                      onClick={() => setShowAllSizes(!showAllSizes)}
                      className="text-gray-400 mt-1 text-xs font-semibold hover:text-brand-600"
                    >
                      {showAllSizes ? "Show Less" : "Show More"}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Brand Filter */}
            {brands && brands.length > 0 && (
              <div className="mb-5 pb-5 border-b border-gray-100">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Brands</p>
                <div className="flex flex-col items-start gap-2.5">
                  {brands.slice(0, showAllBrands ? brands.length : 4).map((brand) => (
                    <button
                      key={brand.brand_id}
                      className="flex items-center gap-2.5 text-sm text-gray-700 text-left"
                      onClick={() => toggleFilter("brand", brand.brand_name)}
                    >
                      <span className={`w-4 h-4 rounded flex items-center justify-center border transition-colors
                        ${filters.brand.includes(brand.brand_name) ? "bg-brand-600 border-brand-600" : "border-gray-300"}`}>
                        {filters.brand.includes(brand.brand_name) && <span className="text-white text-[10px] leading-none">✓</span>}
                      </span>
                      {brand.brand_name}
                    </button>
                  ))}

                  {brands.length > 4 && (
                    <button
                      onClick={() => setShowAllBrands(!showAllBrands)}
                      className="text-gray-400 mt-1 text-xs font-semibold hover:text-brand-600"
                    >
                      {showAllBrands ? "Show Less" : "Show More"}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Color Filter */}
            {colors && (
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Colors</p>
                {colors.length > 0 ? (
                  <div className="flex flex-wrap items-center gap-2.5">
                    {colors.slice(0, showAllColors ? colors.length : 8).map((color) => (
                      <button
                        key={color.color_id}
                        onClick={() => toggleFilter("color_id", color.color_id)}
                        className={`w-7 h-7 rounded-full border-2 transition-all ${filters.color_id.includes(color.color_id) ? "border-brand-600 scale-110" : "border-gray-200"}`}
                        style={{ background: color.color_name || "#000000" }}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400">No colors available</p>
                )}

                {colors.length > 8 && (
                  <button
                    onClick={() => setShowAllColors(!showAllColors)}
                    className="text-gray-400 mt-3 text-xs font-semibold hover:text-brand-600"
                  >
                    {showAllColors ? "Show Less" : "Show More"}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Mobile filter trigger — sits just above the global bottom nav (BottomFixedNav) */}
          <button onClick={() => setOpenFilter(true)}
            className="md:hidden fixed bottom-16 left-0 right-0 z-[70] flex items-center justify-center gap-2 py-3.5 bg-brand-600 text-white text-sm font-bold shadow-[0_-4px_12px_rgba(0,0,0,0.1)]">
            <FiFilter size={16} /> Filters
            {(filters.size.length + filters.brand.length + filters.color_id.length + filters.category.length) > 0 && (
              <span className="bg-white text-brand-600 text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {filters.size.length + filters.brand.length + filters.color_id.length + filters.category.length}
              </span>
            )}
          </button>

          {openFilter && (
            <div className='fixed w-full left-0 top-0 h-screen bg-black/50 z-[75]' onClick={() => setOpenFilter(false)}></div>
          )}

          {/* Mobile filter bottom sheet */}
          <div
            className={`${!openFilter ? 'hidden' : 'fixed'} md:hidden flex-col gap-4 h-[70vh] overflow-y-auto z-[80] bg-white w-full left-0 rounded-t-3xl bottom-0 p-5`}>
            <div className="flex justify-between items-center mb-2">
              <span className="text-base font-bold text-gray-900">Filters</span>
              <button className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500" onClick={() => setOpenFilter(false)}>
                <FiX size={16} />
              </button>
            </div>

            {sizes && sizes.length > 0 && (
              <div className="pb-4 border-b border-gray-100">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Sizes</p>
                <div className="flex flex-wrap gap-2">
                  {sizes.map((sizeObj) => (
                    <button
                      key={sizeObj.size_id}
                      onClick={() => toggleFilter("size", sizeObj.size)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors
                        ${filters.size.includes(sizeObj.size) ? "bg-brand-600 text-white border-brand-600" : "border-gray-200 text-gray-600"}`}
                    >
                      {sizeObj.size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {brands && brands.length > 0 && (
              <div className="py-4 border-b border-gray-100">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Brands</p>
                <div className="flex flex-wrap gap-2">
                  {brands.map((brand) => (
                    <button
                      key={brand.brand_id}
                      onClick={() => toggleFilter("brand", brand.brand_name)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors
                        ${filters.brand.includes(brand.brand_name) ? "bg-brand-600 text-white border-brand-600" : "border-gray-200 text-gray-600"}`}
                    >
                      {brand.brand_name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {colors && colors.length > 0 && (
              <div className="py-4">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Colors</p>
                <div className="flex flex-wrap items-center gap-2.5">
                  {colors.map((color) => (
                    <button
                      key={color.color_id}
                      onClick={() => toggleFilter("color_id", color.color_id)}
                      className={`w-8 h-8 rounded-full border-2 transition-all ${filters.color_id.includes(color.color_id) ? "border-brand-600 scale-110" : "border-gray-200"}`}
                      style={{ background: color.color_name || "#000000" }}
                    />
                  ))}
                </div>
              </div>
            )}

            <button
              onClick={() => setOpenFilter(false)}
              className="mt-2 w-full bg-brand-600 text-white font-semibold py-3 rounded-xl"
            >
              Show {filteredProducts.length} Results
            </button>
          </div>
        </div>


        {/* Product Listing */}
        <div className="all_products w-full md:w-4/5">
          <div className="flex justify-end md:justify-between items-center mb-5">
            {/* Hidden on mobile — MobileHeader's title/ProductsCount props already show this */}
            <div className="hidden md:flex items-center gap-2.5">
              <FiPackage className="text-brand-600" size={20} />
              <h1 className="font-bold text-lg md:text-xl text-gray-900">
                <span className="capitalize">{formattedCategory}</span>
                <span className="text-gray-400 font-medium text-sm md:text-base ml-2">({filteredProducts.length} Products)</span>
              </h1>
            </div>

            {/* Sort control */}
            <div className="relative">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-700 shadow-sm hover:border-gray-300 transition-colors"
              >
                <span className="whitespace-nowrap overflow-hidden text-ellipsis max-w-[110px] md:max-w-none">
                  {selectedLabel}
                </span>
                {isOpen ? <FiChevronUp size={15} /> : <FiChevronDown size={15} />}
              </button>

              {isOpen && (
                <div className='fixed inset-0 bg-black/40 z-[70] md:hidden' onClick={() => setIsOpen(false)}></div>
              )}

              {isOpen && (
                <div className="fixed w-full left-0 bottom-0 md:bottom-auto md:absolute md:left-auto right-0 md:mt-2 bg-white
                  shadow-xl rounded-t-2xl md:rounded-2xl border border-gray-100 p-3 md:w-52 z-[75]">
                  <div className="flex justify-between items-center mb-2 md:hidden px-1">
                    <span className="text-sm font-bold text-gray-900">Sort By</span>
                    <button className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 text-gray-500" onClick={() => setIsOpen(false)}>
                      <FiX size={14} />
                    </button>
                  </div>
                  {sortOptions.map((option) => (
                    <label key={option.value}
                      className={`flex items-center gap-2.5 cursor-pointer px-3 py-2.5 rounded-xl text-sm transition-colors
                      ${sortOption === option.value ? "bg-brand-600/10 text-brand-600 font-semibold" : "text-gray-600 hover:bg-gray-50"}`}>
                      <input
                        type="radio"
                        name="sort"
                        value={option.value}
                        checked={sortOption === option.value}
                        onChange={handleSortChange}
                        className="accent-brand-600"
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {catagoryProducts.category_data?.cat_banner && (
            <img className="w-full rounded-2xl mb-5 hidden md:block" loading="lazy" alt="cat-banner" src={catagoryProducts.category_data.cat_banner} />
          )}

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                className="rounded-2xl border border-gray-100 bg-white overflow-hidden shadow-sm hover:shadow-md transition-shadow relative"
              >
                <Link to={`/p/${product.slug}`} className="relative block aspect-[4/5] bg-gray-50">
                  <LazyLoadImage
                    src={compressImage(product.primary_img, 400, 70, 'webp')}
                    alt={product.prod_name}
                    effect="blur"
                    width="100%"
                    height="100%"
                    className="w-full h-full object-cover"
                    wrapperClassName="w-full h-full block"
                  />
                </Link>
                <div className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-white/90 shadow-sm flex items-center justify-center">
                  <WishlistButton
                    product={product}
                    wishlist={wishlist}
                    setWishlist={setWishlist}
                    token={token}
                    API_BASE_URL={API_BASE_URL}
                  />
                </div>

                <div className="p-2.5 md:p-4">
                  {product.brand_details?.brand_name && (
                    <h3 className="text-[10px] md:text-xs text-gray-400 font-semibold uppercase tracking-wide truncate">
                      {product.brand_details.brand_name}
                    </h3>
                  )}
                  <h2 className="text-xs md:text-sm text-gray-900 truncate font-semibold mt-0.5">
                    {product.prod_name}
                  </h2>

                  {UserRole.userRole === 'distributor' ? (
                    (() => {
                      const { packPrice, packQty } = getPackInfo(product);
                      if (!packPrice || !packQty) return null;
                      return (
                        <div className="flex flex-col mt-1.5 text-[10px] md:text-xs text-gray-500">
                          <div className="font-bold text-gray-900 text-sm">
                            ₹{packPrice}
                          </div>
                          <div>
                            Pack of {packQty}
                          </div>
                        </div>
                      );
                    })()
                  ) : (
                    <div className="flex gap-1.5 md:gap-2 items-end mt-1.5 flex-wrap">
                      <p className="text-gray-900 text-xs md:text-sm font-bold">
                        ₹{product.sale_price}
                      </p>
                      {product.regular_price > product.sale_price && (
                        <p className="text-gray-400 text-[11px] line-through md:text-sm">
                          ₹{product.regular_price}
                        </p>
                      )}
                      {product.regular_price > product.sale_price && (
                        <p className="text-green-600 font-bold text-[10px] md:text-xs">
                          {(
                            ((product.regular_price - product.sale_price) / product.regular_price) *
                            100
                          ).toFixed(0)}% OFF
                        </p>
                      )}
                    </div>
                  )}

                  {(product.product_tag || product.product_quality) && (
                    <div className='flex gap-1.5 mt-2 flex-wrap'>
                      {(product.product_tag && product.product_tag !== 'null') && (
                        <span className='px-2 py-0.5 rounded-md bg-green-100 text-green-700 font-semibold uppercase text-[9px] md:text-[10px]'>
                          {product.product_tag}
                        </span>
                      )}
                      {(product.product_quality && product.product_quality !== 'null') && (
                        <span className='px-2 py-0.5 rounded-md bg-gray-100 text-gray-600 font-semibold uppercase text-[9px] md:text-[10px]'>
                          {product.product_quality}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProductPageBody;
